// Shared product card, used by the home "new products" rail and the all-products grid.
import { Box, Flex, Image, Text, useColorModeValue } from "@chakra-ui/react";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SpecChip = ({ children, accent }) => {
  const border = useColorModeValue("#E1E7F0", "whiteAlpha.300");
  const color = useColorModeValue("#6B7A90", "gray.400");
  const accentBorder = useColorModeValue("rgba(25, 65, 143, 0.32)", "whiteAlpha.400");
  const accentColor = useColorModeValue("brand.500", "brand.300");
  const accentBg = useColorModeValue("rgba(25, 65, 143, 0.05)", "whiteAlpha.100");

  return (
    <Text
      as="span"
      fontFamily="mono"
      fontSize="9.5px"
      letterSpacing="0.08em"
      textTransform="uppercase"
      lineHeight="1.4"
      border="1px solid"
      borderColor={accent ? accentBorder : border}
      color={accent ? accentColor : color}
      bg={accent ? accentBg : "transparent"}
      borderRadius="2px"
      px="6px"
      py="2.5px"
    >
      {children}
    </Text>
  );
};

const ProductCard = ({ product, image }) => {
  const { t } = useTranslation();

  const cardBg = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("#E1E7F0", "whiteAlpha.200");
  const hoverBorder = useColorModeValue("#B9CCE4", "whiteAlpha.400");
  const imageBg = useColorModeValue("#F2F6FB", "whiteAlpha.50");
  const modelColor = useColorModeValue("#101A2E", "white");
  const descColor = useColorModeValue("#6B7A90", "gray.400");

  if (!product) return null;

  // Descriptions are spec-style model descriptors ("4K 8M AI IR PTZ Camera") and stay in
  // English unless a locale supplies an override in its products.json.
  const description = t(`products:${product.id}.description`, {
    defaultValue: product.description,
  });
  const specs = (product.badges || []).slice(0, 4);

  return (
    <Flex
      as={RouterLink}
      to={`/product/${product.category}/${product.id}`}
      direction="column"
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="8px"
      overflow="hidden"
      transition="border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease"
      _hover={{
        borderColor: hoverBorder,
        boxShadow: "0 10px 26px rgba(16, 26, 46, 0.10)",
        transform: "translateY(-2px)",
        textDecoration: "none",
      }}
      _focusVisible={{
        outline: "2px solid",
        outlineColor: "brand.400",
        outlineOffset: "2px",
      }}
    >
      {/* Product photos are normalized to an 800x800 canvas with 8% margin baked in
          (scripts/normalize-product-images.py), so a square frame needs almost no padding. */}
      <Box
        position="relative"
        bg={imageBg}
        sx={{ aspectRatio: "1 / 1" }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p="6px"
      >
        {product.isNew && (
          <Text
            as="span"
            position="absolute"
            top="9px"
            left="9px"
            zIndex="1"
            fontFamily="mono"
            fontSize="9px"
            fontWeight="700"
            letterSpacing="0.11em"
            textTransform="uppercase"
            bg="signal.500"
            color="#0A1020"
            borderRadius="2px"
            px="7px"
            py="3px"
          >
            {t("common:productCard.new")}
          </Text>
        )}
        <Text
          as="span"
          position="absolute"
          top="9px"
          right="9px"
          zIndex="1"
          fontFamily="mono"
          fontSize="9px"
          letterSpacing="0.11em"
          textTransform="uppercase"
          bg="rgba(16, 26, 46, 0.72)"
          color="#DCE6F4"
          borderRadius="2px"
          px="7px"
          py="3px"
        >
          {t(`common:categories.${product.category}`)}
        </Text>
        {image && (
          <Image
            src={image}
            alt={product.model}
            maxH="100%"
            w="auto"
            objectFit="contain"
            loading="lazy"
          />
        )}
      </Box>

      <Flex direction="column" gap="7px" p="15px 16px 17px" flex="1">
        <Text
          fontFamily="mono"
          fontSize="14px"
          fontWeight="700"
          lineHeight="1.25"
          color={modelColor}
        >
          {product.model}
        </Text>
        <Text fontSize="13px" lineHeight="1.5" color={descColor} flex="1">
          {description}
        </Text>
        {specs.length > 0 && (
          <Flex gap="5px" flexWrap="wrap" mt="2px">
            {specs.map((badge, i) => (
              <SpecChip key={badge.text} accent={i === 0}>
                {badge.text}
              </SpecChip>
            ))}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default ProductCard;
